import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, X, Sparkles, Zap, TrendingUp, Lock, Briefcase, CheckCircle, Gift } from 'lucide-react';
import api from '../../utils/api';

const ICON_MAP = { Sparkles, Zap, TrendingUp, Lock, Briefcase, CheckCircle, Gift };
const ICON_OPTIONS = Object.keys(ICON_MAP);

const FeatureManagement = () => {
  const [features, setFeatures] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    icon_name: 'Sparkles',
    order: 0,
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await api.get('/features');
      setFeatures(response.data);
    } catch (error) {
      console.error('特徴の取得に失敗しました:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/features/${editing.id}`, formData);
        toast({ title: '更新完了', description: '特徴を更新しました' });
      } else {
        await api.post('/features', formData);
        toast({ title: '作成完了', description: '新しい特徴を作成しました' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchFeatures();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '操作に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData({
      title: item.title,
      description: item.description,
      content: item.content,
      icon_name: item.icon_name || 'Sparkles',
      order: item.order || 0,
      is_active: item.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    try {
      await api.delete(`/features/${id}`);
      toast({ title: '削除完了', description: '特徴を削除しました' });
      fetchFeatures();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '削除に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      icon_name: 'Sparkles',
      order: 0,
      is_active: true,
    });
    setEditing(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            私たちの強み管理
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {!isEditMode ? (
              <Button
                onClick={() => setIsEditMode(true)}
                variant="outline"
                className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                data-testid="feature-edit-mode-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                編集モード
              </Button>
            ) : (
              <Button
                onClick={() => setIsEditMode(false)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-2" />
                編集終了
              </Button>
            )}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={resetForm}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
                  data-testid="feature-new-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  新規作成
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30 text-gray-900">
                <DialogHeader>
                  <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                    {editing ? '特徴を編集' : '新しい特徴を作成'}
                  </DialogTitle>
                  <DialogDescription className="text-gray-600">
                    フロントページの「私たちの強み」セクションに表示される項目です
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title" className="text-gray-600">タイトル</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="例: 革新性"
                        className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="icon_name" className="text-gray-600">アイコン</Label>
                      <Select
                        value={formData.icon_name}
                        onValueChange={(v) => setFormData({ ...formData, icon_name: v })}
                      >
                        <SelectTrigger className="bg-white/50 border-amber-500/30 text-gray-900">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-amber-500/30 text-gray-900">
                          {ICON_OPTIONS.map((name) => {
                            const Icon = ICON_MAP[name];
                            return (
                              <SelectItem key={name} value={name}>
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  <span>{name}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-gray-600">短い説明（カードに表示）</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="例: 最新技術を活用した革新的なアプローチ"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content" className="text-gray-600">詳細内容（ポップアップに表示）</Label>
                    <Textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={8}
                      placeholder="クリック時のポップアップで表示される詳細な本文"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 items-end">
                    <div className="space-y-2">
                      <Label htmlFor="order" className="text-gray-600">表示順 (小さい順)</Label>
                      <Input
                        id="order"
                        type="number"
                        value={formData.order}
                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                        className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                      <Label htmlFor="is_active" className="text-gray-600">公開する</Label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDialogClose}
                      className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                    >
                      キャンセル
                    </Button>
                    <Button
                      type="submit"
                      className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                      disabled={loading}
                    >
                      {loading ? '処理中...' : (editing ? '更新' : '作成')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {features.map((feature) => {
            const Icon = ICON_MAP[feature.icon_name] || Sparkles;
            return (
              <div
                key={feature.id}
                className="border border-amber-500/20 rounded-lg p-4 hover:border-amber-500/50 bg-white/30 transition-all backdrop-blur-sm"
              >
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-gray-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs text-gray-500 font-mono">#{feature.order}</span>
                      <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${feature.is_active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-100/20 text-gray-600 border border-gray-300/30'}`}>
                        {feature.is_active ? '公開中' : '非公開'}
                      </span>
                    </div>
                    <p className="text-amber-500 text-sm mb-1">{feature.description}</p>
                    <p className="text-gray-600 text-sm line-clamp-2">{feature.content}</p>
                  </div>
                  {isEditMode && (
                    <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(feature)}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                        data-testid={`feature-edit-${feature.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(feature.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {features.length === 0 && (
            <div className="text-center text-gray-600 py-8">
              特徴がまだ登録されていません
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeatureManagement;
